import i18n from "i18next";
import "../../i18n";
import { account, main, search } from "../../constants/actionTypes";
import { actions as accountActions } from "../account";
import { modalsName } from "../../constants/account";
import { getSavedContactFields, markQuestionFormAnswered } from "../../helpers/questionForm";
import { reportGaSignUp } from "../../helpers/gaSignUp";
import { setGaUserId } from "../../helpers/gaUserId";
import { setGaTrafficType } from "../../helpers/gaTrafficType";
import { setGaUserProperties } from "../../helpers/gaUserProperties";
import { ensurePartnero } from "../../helpers/partneroLoader";
import { hasPartneroSignupToSend, sendPartneroSignup } from "../../helpers/partneroSignup";
import { verificationConfirmErrorCopy } from "../../helpers/verificationConfirmError";
import apiClient from "../../api/client";

let profileRequest = null;

// Etag for the static reference slice of the dashboard bootstrap payload.
// Kept in memory (not persisted) so it shares the lifecycle of the reference
// data in the in-memory Redux store: both reset on reload, so a fresh load
// always refetches the full reference payload, while repeated bootstrap calls
// within a session reuse the etag and skip the unchanged reference data.
let referenceEtag = null;

// User the Partnero signup event was already attempted for in this page session, so a second
// bootstrap that lands before the backend cleared the flag stays quiet.
let partneroSignupSentFor = null;

const reportPartneroSignup = profile => {
  if (!hasPartneroSignupToSend(profile) || partneroSignupSentFor === profile.user_id) {
    return;
  }
  // Claimed before the script is loaded, so a bootstrap that lands while it loads stays quiet.
  partneroSignupSentFor = profile.user_id;

  ensurePartnero()
    .then(() => {
      if (!sendPartneroSignup(profile)) {
        partneroSignupSentFor = null;
        return undefined;
      }

      // If this fails the flag stays set and the event is sent again on the next page load.
      return apiClient({
        method: "post",
        responseType: "json",
        url: "/members/api-v2/my-account/partnero-signup"
      }).catch(() => undefined);
    })
    // The Partnero script could not be loaded (blocked, offline): the flag stays set for the next page load.
    .catch(() => undefined);
};

const setDashboardBootstrapLoading = dispatch => {
  dispatch({
    type: main.SET_LOADING,
    payload: {
      section: "utilityListing",
      isSectionLoading: true
    }
  });
  dispatch({
    type: main.SET_LOADING,
    payload: {
      section: "footerInfo",
      isSectionLoading: true
    }
  });
  dispatch({
    type: main.SET_LOADING,
    payload: {
      section: "communities",
      isSectionLoading: true
    }
  });
  dispatch({
    type: search.SET_LOADING,
    payload: {
      section: "search",
      isLoading: true
    }
  });
};

const applyDashboardBootstrap = (dispatch, data = {}) => {
  if (data.countries && data.states) {
    dispatch({
      type: main.GET_UTILITY_LISTING,
      payload: {
        countries: data.countries,
        states: data.states
      }
    });
  }

  if (data.footer) {
    dispatch({
      type: main.GET_FOOTER_INFO,
      payload: {
        footerInfo: data.footer.links || [],
        socialLinks: data.footer.social_links || []
      }
    });
  }

  if (data.communities_tree) {
    dispatch({
      type: main.GET_COMMUNITIES,
      payload: data.communities_tree
    });
  }

  if (data.professions_tree) {
    dispatch({
      type: search.GET_PROFESSIONS,
      payload: data.professions_tree
    });
  }
};

const clearDashboardBootstrapLoading = dispatch => {
  dispatch({ type: main.GET_UTILITY_LISTING_FAIL });
  dispatch({ type: main.GET_FOOTER_INFO_FAIL });
  dispatch({
    type: main.SET_LOADING,
    payload: {
      section: "communities",
      isSectionLoading: false
    }
  });
  dispatch({ type: search.GET_PROFESSIONS_FAIL });
};

export const actions = {
  sendPhoneCode(phone) {
    return dispatch => {
      dispatch(accountActions.setLoading("verifications", true));

      apiClient({
        method: "post",
        url: "members/api-v2/verification/phone/send-code",
        data: { phone },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(({ data }) => {
          dispatch({
            type: account.PHONE_VERIFY_CODE_SUCCESS,
            payload: { token: data?.data?.token || "", phone }
          });
        })
        .catch(() => dispatch({ type: account.PHONE_VERIFY_CODE_FAIL }));
    };
  },
  checkPhoneCode(code, phone) {
    return (dispatch, getState) => {
      dispatch(accountActions.setLoading("verifications", true));
      const token = getState().account.phoneVerificationToken;
      apiClient({
        method: "post",
        url: "members/api-v2/verification/phone/confirm-code",
        data: { code, token, phone },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(() => {
          dispatch(this.updatePhone(phone));
        })
        .catch(error => {
          dispatch({
            type: account.CHECK_PHONE_VERIFY_CODE_FAIL,
            payload: verificationConfirmErrorCopy(error, {
              wrongCode: i18n.t("dashboard_verify_code_incorrect"),
              fallback: i18n.t("dashboard_verify_code_incorrect")
            })
          });
        });
    };
  },

  removePhoneNumber() {
    return dispatch => {
      dispatch(accountActions.setLoading("verifications", true));

      apiClient({
        method: "delete",
        url: "members/api-v2/my-account/phone",
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(() => {
          dispatch({ type: account.REMOVE_PHONE_SUCCESS });
          dispatch(this.getProfile());
        })
        .catch(() => dispatch({ type: account.REMOVE_PHONE_FAIL }));
    };
  },

  updateEmail(email, keycloak) {
    return dispatch => {
      dispatch(accountActions.setLoading("verifications", true));

      apiClient({
        method: "post",
        url: "members/api-v2/my-account/email",
        data: { email },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(() => {
          keycloak
            .updateToken(60)
            .then(() => {
              dispatch({ type: account.UPDATE_EMAIL_SUCCESS, payload: email });
            })
            .catch(() => {
              dispatch({ type: account.CHECK_EMAIL_VERIFY_CODE_FAIL });
            });
        })
        .catch(error => {
          dispatch({
            type: account.EMAIL_VERIFY_CODE_FAIL,
            payload: {
              isShow: true,
              message: error.response?.data?.error || error.response?.data?.message || error.response?.data?.data?.error
            }
          });
        });
    };
  },
  updatePhone(phone) {
    return dispatch => {
      dispatch(accountActions.setLoading("verifications", true));

      apiClient({
        method: "post",
        url: "members/api-v2/my-account/phone",
        data: { phone },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(() => {
          dispatch({ type: account.UPDATE_PHONE_SUCCESS });
          dispatch(this.getProfile());
        })
        .catch(() => {
          dispatch({ type: account.UPDATE_EMAIL_FAIL });
        });
    };
  },

  sendEmailCode(email, callback, isNotCloseModal) {
    return dispatch => {
      dispatch(accountActions.setLoading("verifications", true));

      apiClient({
        method: "post",
        url: "members/api-v2/verification/email/send-code",
        data: { email },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(({ data }) => {
          if (isNotCloseModal) {
            dispatch({
              type: account.EMAIL_SEND_VERIFY_CODE_SUCCESS,
              payload: { token: data?.data?.token || "", email }
            });
          } else {
            dispatch({
              type: account.EMAIL_VERIFY_CODE_SUCCESS,
              payload: { token: data?.data?.token || "", email }
            });
          }
        })
        .catch(error => {
          if (callback) {
            callback();
          }
          dispatch({
            type: account.EMAIL_VERIFY_CODE_FAIL,
            payload: {
              isShow: true,
              message: error.response?.data?.error || error.response?.data?.message || error.response?.data?.data?.error
            }
          });
        });
    };
  },

  checkEmailCode(code, newLoginData, keycloak, isVerified) {
    return (dispatch, getState) => {
      dispatch(accountActions.setLoading("verifications", true));
      const token = getState().account.emailVerificationToken;
      apiClient({
        method: "post",
        url: "members/api-v2/verification/email/confirm-code",
        data: { code, token, email: newLoginData.email },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(() => {
          if (isVerified) {
            dispatch({ type: account.VERIFIED_EMAIL_SUCCESS, payload: newLoginData.email });
          } else {
            dispatch(this.updateEmail(newLoginData.email, keycloak));
          }
        })
        .catch(error => {
          dispatch({
            type: account.CHECK_EMAIL_VERIFY_CODE_FAIL,
            payload: verificationConfirmErrorCopy(error, {
              wrongCode: i18n.t("dashboard_verify_code_incorrect_caps"),
              fallback: i18n.t("dashboard_verify_code_incorrect_caps")
            })
          });
        });
    };
  },

  passwordVerificationSuccess() {
    return {
      type: account.PASSWORD_VERIFICATION_SUCCESS
    };
  },

  getProfile() {
    return dispatch => {
      dispatch(accountActions.setLoading("profile", true));
      // Only arm the reference-data loaders when we will actually (re)fetch the
      // reference payload. When an etag is already cached, the reference data is
      // already in the store and the backend responds `reference_unchanged: true`
      // without the reference success actions that clear these loaders — setting
      // them here would leave the loading state stuck on.
      if (!referenceEtag) {
        setDashboardBootstrapLoading(dispatch);
      }
      profileRequest =
        profileRequest ||
        apiClient
          .get("/members/api-v2/dashboard-bootstrap", {
            headers: referenceEtag ? { "X-Reference-Etag": referenceEtag } : {}
          })
          .finally(() => {
            profileRequest = null;
          });

      profileRequest
        .then(res => {
          const data = res.data?.data || {};
          const profile = data.profile || {};

          const nextReferenceEtag = data.cache?.reference_etag;
          if (nextReferenceEtag) {
            referenceEtag = nextReferenceEtag;
          }

          applyDashboardBootstrap(dispatch, data);

          dispatch({
            type: account.GET_PROFILE,
            payload: profile
          });

          // Before the sign_up push, so that event carries the User-ID, user properties and traffic type too.
          setGaUserId(profile);
          setGaUserProperties(profile);
          setGaTrafficType(profile);
          reportPartneroSignup(profile);
          reportGaSignUp(profile);
        })
        .catch(err => {
          clearDashboardBootstrapLoading(dispatch);
          dispatch({
            type: account.GET_PROFILE_FAIL,
            payload: err
          });
        });
    };
  },

  /**
   * Re-reads `profile_completion` (GROWTH-7134) so the My Feed "Needs your attention" card shows the score as it is now,
   * not as it was when the dashboard loaded. GET /api-v2/my-account computes it outside the 5 s profile cache, so a
   * save made a moment ago is already in it. It does not re-issue the dashboard bootstrap, so no loader flips on.
   *
   * A failed or malformed response leaves the stored score as it was, with no error shown.
   *
   * @returns {Promise<void>} Settles once the score is stored or the attempt is dropped. It never rejects.
   */
  getProfileCompletion() {
    return dispatch =>
      Promise.resolve(apiClient({ method: "get", responseType: "json", url: "/members/api-v2/my-account" }))
        .then(result => {
          const profile = result?.data?.data?.profile;
          if (!profile || !("profile_completion" in profile)) {
            return;
          }

          dispatch({ type: account.SET_PROFILE_COMPLETION, payload: profile.profile_completion });
        })
        .catch(() => {});
  },

  saveNewEmailPassword(email, password) {
    return {
      type: account.SAVE_NEW_EMAIL_PASSWORD,
      payload: { email, password }
    };
  },

  cleanNewEmailPassword() {
    return {
      type: account.CLEAN_NEW_EMAIL_PASSWORD
    };
  },

  updateContacts(data, isQuestionForm) {
    return async (dispatch, getState) => {
      dispatch(accountActions.setLoading("contacts", true));
      apiClient({
        method: "post",
        responseType: "json",
        url: "/members/api-v2/my-account/contacts",
        data
      })
        .then(() => {
          // Keep the stored profile in step with what the backend just saved, so
          // the feed pop-up and My Account do not work from stale values.
          dispatch({
            type: account.UPDATE_CONTACTS,
            payload: getSavedContactFields(data)
          });
          if (isQuestionForm) {
            markQuestionFormAnswered(getState().account.profile?.user_id);
            dispatch(this.toggleActivityOfModal(modalsName.questionForm, false));
            dispatch(this.toggleActivityOfModal(modalsName.questionFormSuccess, true));
          }

          dispatch({
            type: account.UPDATE_CONTACTS_SUCCESS,
            payload: true
          });

          setTimeout(
            () =>
              dispatch({
                type: account.UPDATE_CONTACTS_SUCCESS,
                payload: null
              }),
            6000
          );
        })
        .catch(error => {
          dispatch({
            type: account.UPDATE_CONTACTS_FAIL,
            payload: error.response
          });
        })
        .finally(() => {
          dispatch(accountActions.setLoading("contacts", false));
        });
    };
  },

  // Resolves once the request has finished either way, so a caller can hold its form until then (GROWTH-7580).
  updateMyAccountInformation(data) {
    return async dispatch => {
      return apiClient({
        method: "post",
        responseType: "json",
        url: "/members/api-v2/my-account/personal-info",
        data
      })
        .then(() => {
          dispatch({
            type: account.UPDATE_CONTACTS_SUCCESS,
            payload: true
          });

          setTimeout(
            () =>
              dispatch({
                type: account.UPDATE_CONTACTS_SUCCESS,
                payload: null
              }),
            6000
          );
        })
        .catch(error => {
          dispatch({
            type: account.UPDATE_CONTACTS_FAIL,
            payload: error.response
          });
        })
        .finally(() => {
          dispatch(accountActions.setLoading("contacts", false));
        });
    };
  },

  // The Account Settings v2 autosave needs to know whether the save went through: true when it did, false when the request
  // failed (GROWTH-7712). It runs updateMyAccountInformation as it is and reads its outcome off the failure action.
  saveMyAccountInformation(data) {
    return async dispatch => {
      let isSaved = true;

      await actions.updateMyAccountInformation(data)(action => {
        if (action?.type === account.UPDATE_CONTACTS_FAIL) {
          isSaved = false;
        }

        return dispatch(action);
      });

      return isSaved;
    };
  },

  removeNotificationEmail(data) {
    return async dispatch => {
      dispatch(accountActions.setLoading("removeNotificationEmail", true));
      apiClient({
        method: "post",
        responseType: "json",
        url: "/members/json-api/listing/delete-listing-email",
        data
      })
        .then(() => {
          dispatch({
            type: account.NOTIFICATION_REMOVE_EMAIL_SUCCESS,
            payload: { listingId: data.listing_id, prKey: data.prkey }
          });
        })
        .catch(error => {
          dispatch({
            type: account.NOTIFICATION_REMOVE_EMAIL_FAIL,
            payload: error.response
          });
        });
    };
  },

  removeNotificationPhone(data) {
    return async dispatch => {
      dispatch(accountActions.setLoading("removeNotificationPhone", true));
      apiClient({
        method: "post",
        responseType: "json",
        url: "/members/json-api/listing/delete-listing-phone",
        data
      })
        .then(() => {
          dispatch({
            type: account.NOTIFICATION_REMOVE_PHONE_SUCCESS,
            payload: { listingId: data.listing_id, prKey: data.prkey }
          });
        })
        .catch(error => {
          dispatch({
            type: account.NOTIFICATION_REMOVE_PHONE_FAIL,
            payload: error.response
          });
        });
    };
  },

  toggleActivityOfModal(modal, isActive) {
    return {
      type: account.TOGGLE_ACTIVITY_OF_MODAL,
      payload: {
        modal,
        isActive
      }
    };
  },

  clearChangeEmailError() {
    return {
      type: account.EMAIL_VERIFY_CODE_CLEAR_ERROR
    };
  },

  clearAllModals() {
    return {
      type: account.CLEAR_ALL_MODALS
    };
  }
};
